import { faLink, faLocationDot, faPencil, faPlus } from "@fortawesome/free-solid-svg-icons";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { useState } from "react";
import { api } from "../../utils/api";
import { importFiles } from "../../utils/file";
import { useArtistContext } from "./ArtistPageContext";
import ArtistPageHighlights from "./ArtistPageFragments/ArtistPageHighlights";
import ContactModal from "./ArtistPageModals/ContactModal";
import HighlightsModal from "./ArtistPageModals/HighlightsModal";
import PersonalInfoModal from "./ArtistPageModals/PersonalInfoModal";
import ShareModal from "./ArtistPageModals/ShareModal";
import SocialsModal from "./ArtistPageModals/SocialsModal";
import TipJarModal from "./ArtistPageModals/TipJarModal";
import { useCurrentUser } from "../../UserProvider";
import { Link } from "react-router-dom"; 

function SocialLink({ social, link }) {
  let cleanLink = link.trim();

  if (/^https?:\/\//i.test(cleanLink)) {
    cleanLink = cleanLink.replace(/\/+$/, "");
  } else {
    switch (social.toLowerCase()) {
      case "instagram":
        cleanLink = cleanLink.replace(/^@/, "");
        cleanLink = `https://www.instagram.com/${cleanLink}`;
        break;

      case "tiktok":
        cleanLink = cleanLink.replace(/^@/, "");
        cleanLink = `https://www.tiktok.com/@${cleanLink}`;
        break;

      case "spotify":
        cleanLink = `https://open.spotify.com/artist/${cleanLink}`;
        break;

      case "deezer":
        cleanLink = `https://www.deezer.com/artist/${cleanLink}`;
        break;

      case "youtube":
        cleanLink = cleanLink.replace(/^@/, "");
        cleanLink = `https://www.youtube.com/${cleanLink}`;
        break;

      default:
        break;
    }
  }

  return (
    <div className="social">
      <a href={ cleanLink } target="_blank" rel="noopener noreferrer">
        <img src={ `/images/${social}_logo.png` } alt="" />
      </a>
    </div>
  );
}

export default function ArtistPageContent() {
  const { isOwner, artist, setArtist } = useArtistContext();
  const { currentUser, refreshUser } = useCurrentUser();
  const [showPersonalInfoModal, setShowPersonalInfoModal] = useState(false);
  const [showHighlightsModal, setShowHighlightsModal] = useState(false);
  const [showTipJarModal, setShowTipJarModal] = useState(false);
  const [showSocialsModal, setShowSocialsModal] = useState(false);
  const [showShareModal, setShowShareModal] = useState(false);
  const [showContactModal, setShowContactModal] = useState(false);
  const [uploading, setUploading] = useState(false);
  const [showMore, setShowMore] = useState(false);

  const TOTAL_DEFAULT_AVATARS = 5; // Update this if you add more default avatars to the public/images/avatars directory
  const getAvatarImage = () => {
    if (artist?.avatar_url || artist?.bubble_image_url) {
      return artist.avatar_url || artist.bubble_image_url;
    }
    
    const baseId = artist?.user_id || artist?.id || 0;
    const imageIndex = baseId % TOTAL_DEFAULT_AVATARS;
    return `/images/avatars/default-${imageIndex}.png`;
  };

  const displayImage = getAvatarImage(); 

  const handleSave = async (updatedData) => {
    try {
      const { data: updatedArtist } = await api.put(`/profiles/${artist.id}`, updatedData);
      setArtist(updatedArtist);
    } catch (err) {
      console.error("Failed to save profile:", err);
    }
  };

  const handleImageChange = async () => {
    try {
      const fileList = await importFiles(["image/*"], false);

      if (fileList.length === 0) {
        return;
      }

      const file = fileList.item(0);
      setUploading(true);
      const ext = file.name.split(".").pop();

      const { data } = await api.post("/uploads/avatar", {
        profile_id: artist.id,
        file_extension: ext,
      });

      const response = await fetch(data.upload_url, {
        method: "PUT",
        headers: { "Content-Type": file.type },
        body: file,
      });

      if (!response.ok) {
        throw new Error("Upload failed");
      }

      await handleSave({ avatar_extension: ext });
      await refreshUser();
    } catch (err) {
      console.error(err);
    } finally {
      setUploading(false);
    }
  };

  return (
    <div className="__artist">
      <div className="container">
        <div className="dashboard">
          <div className="dashboard__header header">
            <div className="header__personal-info personal-info">
              { artist.name && (
                <p className="personal-info__name">
                  { artist.name }
                </p>
              ) }
              { !artist.name && isOwner && (
                <button className="button button--outline button--white" onClick={ () => setShowPersonalInfoModal(true) }>
                  Add Name <FontAwesomeIcon icon={ faPlus } />
                </button>
              ) }
              { artist.address && (
                <p className="personal-info__location">
                  <FontAwesomeIcon icon={ faLocationDot } /> { artist.address }
                </p>
              ) }
              { !artist.address && isOwner && (
                <button className="button button--outline button--white" onClick={ () => setShowPersonalInfoModal(true) }>
                  Add Location <FontAwesomeIcon icon={ faPlus } />
                </button>
              ) }
            </div>
            { isOwner && (
              <div className="header__button-group">
                <button className="button button--outline button--white" onClick={ () => setShowPersonalInfoModal(true) }>
                  Edit <FontAwesomeIcon icon={ faPencil } />
                </button>
                <button className="button button--fill button--primary" onClick={ () => setShowShareModal(true) }>
                  Share <FontAwesomeIcon icon={ faLink } />
                </button>
              </div>
            ) }
            { !isOwner && (
              <div className="header__button-group">
                { currentUser ? (
                  <Link to={`/inbox/${artist.display_name}`} className="ppy-btn ppy-btn-primary">
                    Contact Me
                  </Link>
                ) : (
                  <button className="ppy-btn ppy-btn-primary" onClick={ () => setShowContactModal(true) }>
                    Contact Me
                  </button>
                ) }
              </div>
            ) }
          </div>
          { isOwner && (
            <div className="dashboard__navigation">
              <button type="button" className="button button--fill button--white">
                Profile
              </button>
              <button type="button" className="button button--outline button--white">
                Application
              </button>
              <button type="button" className="button button--outline button--white">
                Contacts
              </button>
            </div>
          ) }
          <div className="dashboard__grid grid">
            <div className="grid__column column column--gravatar">
              <div className="gravatar" style={{ backgroundImage: `url(${ displayImage })` }}>
                <div className="gravatar__content content">
                  <div className="content__head head">
                    { isOwner && (
                      <button
                        type="button"
                        onClick={ handleImageChange }
                        disabled={ uploading }
                        className="head__button button button--fill button--primary button--icon"
                      >
                        <FontAwesomeIcon icon={ faPencil } />
                      </button>
                    ) }
                  </div>
                  <div className="content__body body">
                    { (artist.genres && artist.genres.length !== 0) && (
                      <ul className="body__pill-list pill-list">
                        { artist.genres.map((genre, index) => (
                          <li key={index} className="pill-list__item">
                            { genre }
                          </li>
                        )) }
                      </ul>
                    ) }
                    <p className="body__description description">
                      { artist.bio? artist.bio.slice(0, showMore ? undefined : 200) : "No bio available." }
                    </p>
                    { artist.bio && (
                      <div className="body__button-group">
                        <button type="button" onClick={ () => setShowMore(!showMore) } className="button button--fill button--primary">
                          { showMore ? "Show Less" : "Show More" }
                        </button>
                      </div>
                    ) }
                  </div>
                </div>
              </div>
            </div>
            <ArtistPageHighlights
              artist={ artist }
              onShowModal={ () => setShowHighlightsModal(true) }
            />
            { (isOwner || artist.spotify_link || artist.deezer_link || artist.youtube_link || artist.instagram_link || artist.tiktok_link) && (
              <div className="grid__column column column--default">
                <div className="column__social-group">
                  { isOwner && (
                    <button className="button button--primary button--fill button--icon" onClick={ () => setShowSocialsModal(true) }>
                      <FontAwesomeIcon icon={ faPencil } />
                    </button>
                  ) }
                  { artist.spotify_link && (
                    <SocialLink social="spotify" link={ artist.spotify_link } />
                  ) }
                  { artist.deezer_link && (
                    <SocialLink social="deezer" link={ artist.deezer_link } />
                  ) }
                  { artist.youtube_link && (
                    <SocialLink social="youtube" link={ artist.youtube_link } />
                  ) }
                  { artist.instagram_link && (
                    <SocialLink social="instagram" link={ artist.instagram_link } />
                  ) }
                  { artist.tiktok_link && (
                    <SocialLink social="tiktok" link={ artist.tiktok_link } />
                  ) }
                </div>
              </div>
            ) }
          </div>
        </div>
      </div>
      <PersonalInfoModal
        show={ showPersonalInfoModal }
        handleClose={ () => setShowPersonalInfoModal(false) }
        onSave={ handleSave }
      />
      <HighlightsModal
        show={ showHighlightsModal }
        handleClose={ () => setShowHighlightsModal(false) }
        onSave={ handleSave }
      />
      <TipJarModal
        show={ showTipJarModal }
        handleClose={ () => setShowTipJarModal(false) }
        onSave={ handleSave }
      />
      <SocialsModal
        show={ showSocialsModal }
        handleClose={ () => setShowSocialsModal(false) }
        onSave={ handleSave }
      />
      <ShareModal
        show={ showShareModal }
        handleClose={ () => setShowShareModal(false) }
        artist={ artist }
      />
      <ContactModal
        show={ showContactModal }
        handleClose={ () => setShowContactModal(false) }
        artist={ artist }
      />
    </div>
  );
}
